import { DOWN_ARROW, UP_ARROW } from "../symbols.js";
export const LOST_MODE_C_TICK_THRESHOLD = 5;
export const CODE_BLINK_VALUES = new Map([
    [0o7777, "AFIO"], // DOD interceptor aircraft on active air defense missions and operating without ATC clearance
    [0o7700, "EMRG"],
    [0o7600, "RDOF"],
    [0o7500, "HIJK"],
    [0o7400, "LLNK"], // unmanned aircraft lost communication link
]);
export const blinkPriorityMap = new Map([
    ["handoffAccepted", 1],
    ["handoffPending", 1],
    ["forcedTrackControl", 1],
    ["coast", 2],
    ["beacon", 2],
    ["MSAW", 3],
    ["facilityData", 4],
    ["sidestreamHandoff", 5],
    ["planData", 6],
    ["hold", 7],
    ["groundspeed", 8],
    ["dest", 9],
]);
export function getBlinkPriority(type) {
    return blinkPriorityMap.get(type);
}
export function formatAdsbAddress(address) {
    return address.toString(16).padStart(6, "0").toLowerCase();
}
export class EramTrack {
    constructor(id, target, additionalProps) {
        Object.assign(this, additionalProps);
        this.id = id;
        this.target = target;
        this.currentSquawk = target?.beacon ?? 0;
        this.callsign = target?.adsbCallsign?.trim() ?? null;
        this.lastTrackUpdate = Date.now();
    }
    id;
    favRecord;
    target = null;
    enteredAoi = false;
    position = [0, 0];
    currentSSCId = null;
    suppressAutoHandoffs = false;
    isUplinkEligible = false;
    currentSquawk = null;
    reachedAssignedAlt = false;
    transponderModes = ["ModeA", "ModeC"];
    owner = null;
    ownerShort = null;
    lostModeCTicks = 0;
    lastTargetUpdate = null;
    lastTrackUpdate;
    isFlatTrack = null;
    // correlated flightplan id
    fpId = null;
    targetSymbol = "uncorrelatedPrimary";
    histories = [];
    coasting = false;
    frozen = false;
    fieldEOverrideValue = null;
    fieldEOverrideType = null;
    callsign = null;
    modeCAltitude = null;
    modeCCorrected = false;
    manuallyDropped = false;
    static update(track, d) {
        if (typeof d === "function") {
            Object.assign(this, d(track));
        }
        else {
            Object.assign(track, d);
        }
        track.lastTrackUpdate = Date.now();
        return track;
    }
    static getTargetSymbol(track, mciFloor, mciUpper) {
        if (track.transponderModes.includes("Standby") || track.transponderModes.length === 0) {
            // primary target
            if (track.fpId) {
                return "correlatedPrimary";
            }
            return "uncorrelatedPrimary";
        }
        if (track.target?.identing) {
            return "identingBeacon";
        }
        if (!track.fpId) {
            if (track.target &&
                track.target.altitudeBaro / 100 > mciFloor &&
                track.target.altitudeBaro / 100 < mciUpper) {
                return "modeCIntruder";
            }
            if (track.currentSquawk === 0o1200) {
                return "vfrBeacon";
            }
            return "uncorrelatedBeacon";
        }
        if (track.target && track.target.altitudeBaro < 23000) {
            return "reducedSeparation";
        }
        return "correlatedBeacon";
    }
}
export function getFieldBAndC(track, coordinationData, assignedAlt) {
    let fieldBText = "";
    let fieldCText = "";
    let b4Value = "";
    const currentAlt = track.modeCAltitude ? Math.round(track.modeCAltitude / 100) : null; // in 100s of ft
    const isStandby = track.transponderModes.length === 0 || track.transponderModes.includes("Standby");
    if (coordinationData?.localInterimAltitude) {
        fieldBText = coordinationData.localInterimAltitude.toString().padStart(2, "0");
        b4Value = "L";
        fieldCText = currentAlt?.toString().padStart(2, "0") ?? "";
    }
    else if (coordinationData?.interimAltitude) {
        fieldBText = coordinationData.interimAltitude.altitude.toString().padStart(2, "0");
        b4Value = coordinationData.interimAltitude.type;
        fieldCText = currentAlt?.toString().padStart(2, "0") ?? "";
    }
    else if (assignedAlt) {
        // no interim alt
        if (assignedAlt.vfr || assignedAlt.vfrPlus) {
            fieldBText = "VFR";
            b4Value = "/";
        }
        else if (assignedAlt.vfrOnTop || assignedAlt.vfrOnTopPlus) {
            fieldBText = "OTP";
            b4Value = "/";
        }
        else if (assignedAlt.above) {
            fieldBText = "ABV";
            b4Value = "/";
        }
        else if (assignedAlt.block) {
            if (!currentAlt) {
                b4Value = "N";
                fieldBText = assignedAlt.block.min.toString().padStart(2, "0");
                fieldCText = assignedAlt.block.max.toString().padStart(2, "0");
            }
            else if (currentAlt < assignedAlt.block.min - 2) {
                b4Value = !track.reachedAssignedAlt ? UP_ARROW : "-";
                fieldBText = assignedAlt.block.max.toString().padStart(2, "0");
                fieldCText = currentAlt.toString().padStart(2, "0");
            }
            else if (currentAlt > assignedAlt.block.max + 2) {
                b4Value = !track.reachedAssignedAlt ? DOWN_ARROW : "+";
                fieldBText = assignedAlt.block.min.toString().padStart(2, "0");
                fieldCText = currentAlt.toString().padStart(2, "0");
            }
            else {
                b4Value = "B";
                fieldBText = assignedAlt.block.min.toString().padStart(2, "0");
                fieldCText = assignedAlt.block.max.toString().padStart(2, "0");
            }
        }
        else if (assignedAlt.simple || assignedAlt.altFixAlt) {
            const _alt = (assignedAlt.simple ?? assignedAlt.altFixAlt?.pre);
            fieldBText = _alt.toString().padStart(2, "0");
            const altDelta = _alt - (currentAlt ?? 0);
            fieldCText = _alt.toString().padStart(2, "0");
            if (Math.abs(altDelta) <= 2) {
                b4Value = assignedAlt.altFixAlt ? "F" : "C";
            }
            else if (!track.reachedAssignedAlt) {
                // up/down arrow in the font
                b4Value = altDelta > 0 ? UP_ARROW : DOWN_ARROW;
            }
            else {
                b4Value = altDelta > 0 ? "-" : "+";
            }
        }
    }
    else {
        b4Value = "V";
    }
    if (isStandby) {
        b4Value = "N";
    }
    else if (track.coasting || track.lostModeCTicks >= LOST_MODE_C_TICK_THRESHOLD) {
        b4Value = "X";
        fieldCText = "XXX";
    }
    else {
        if (!/^[ABCF]$/.test(b4Value) && currentAlt && currentAlt < 180 && !track.modeCCorrected) {
            b4Value = "X";
        }
        if (!assignedAlt?.block && !/^[TLP]$/.test(b4Value)) {
            // do not show current alt if aircraft is at assigned alt
            fieldCText = /^[CAF]$/.test(b4Value) ? "" : (currentAlt ?? "XXX").toString().padStart(2, "0");
        }
    }
    const fieldB4Text = b4Value;
    return { fieldBText, fieldB4Text, fieldCText };
}
//# sourceMappingURL=eramTrack.js.map